import { SyntheticEvent, useCallback, useContext, useState } from "react";

import AuthContext from "context/auth/authContext";
import { TRANSLATION_KEYS } from "shared/constants/translations";
import { useEmailValidation } from "shared/hooks/useEmailValidation";

import { useTogglePswVisibility } from "../../hooks";
import { INPUT_FIELD, PASSWORD_ERRORS, PASSWORD_TYPES } from "../../types";
import { usePasswordValidation } from "./usePasswordValidation";

export const useLogin = () => {
    const { message, loginUser } = useContext(AuthContext);

    const { passwordError, setPasswordError, clearPasswordError } =
        usePasswordValidation();

    const { PasswordVisibility, showPassword } = useTogglePswVisibility(
        PASSWORD_TYPES.Current
    );

    const [user, setUser] = useState({
        email: "",
        password: ""
    });

    const { email, password } = user;

    const {
        isEmailValid,
        emailError,
        setEmailError,
        clearEmailError,
        validateEmail
    } = useEmailValidation(email.trim());

    const clearCredentials = () =>
        setUser({
            email: "",
            password: ""
        });

    const handleLoginChange = useCallback(
        (e: { target: { name: string; value: string } }) => {
            const inputName = e.target.name;
            const inputValue = e.target.value;

            setUser({
                ...user,
                [inputName]: inputValue
            });

            if (inputName === INPUT_FIELD.Email) {
                validateEmail(e);
                clearEmailError();
            } else {
                clearPasswordError(PASSWORD_ERRORS.Password1);
            }
        },
        [user, clearEmailError, clearPasswordError, validateEmail]
    );

    const handleLoginSubmit = (e: SyntheticEvent) => {
        e.preventDefault();

        if (isEmailValid && password.length > 0) {
            loginUser({
                payload: { email: email.trim(), password },
                successCallback: clearCredentials
            });
        } else {
            if (!isEmailValid) {
                setEmailError(TRANSLATION_KEYS.authInvalidEmailFormat);
            }

            if (password.length < 1) {
                setPasswordError(prev => ({
                    ...prev,
                    password1: TRANSLATION_KEYS.authRequired
                }));
            }
        }
    };

    return {
        message,
        isEmailValid,
        emailError,
        passwordError,
        showPassword,
        handleLoginSubmit,
        validateEmail,
        handleLoginChange,
        PasswordVisibility
    };
};
